import {
  PAGE_SHARE_ACCESS_TOKEN_HEADER,
  pagePublicShareUrl,
  revokePagePublicShareUrl,
  sharedPageContentUrl,
  sharedPageStatusUrl,
  sharedPageVerifyUrl,
} from "../constants";
import type {
  ICreatedPagePublicShare,
  ICreatePagePublicShareProps,
  IGetSharedPageContentProps,
  IPagePublicShare,
  IPagePublicShareStatus,
  IPagePublicShareVerifyResponse,
  ISharedPageContent,
  IVerifyPagePublicSharePasswordProps,
} from "../types";
import type { ApiClient } from "./client";

// NOTE: NSAI-5827 — public, password-protected links for AI-generated pages.
// The owner half is session-authenticated and additionally owner-checked on the
// server; the anonymous half is reached by people with no NSAI account at all.
export class PageShareApi {
  constructor(private readonly apiClient: ApiClient) {}

  // NOTE: Owner side.

  async getPagePublicShare(fileUuid: string): Promise<IPagePublicShare | null> {
    const response = await this.apiClient.get<IPagePublicShare | null>(
      pagePublicShareUrl(fileUuid),
    );
    return response.data;
  }

  // NOTE: Creates the link, or — when one already exists — regenerates its
  // password in place, keeping the URL already handed out working.
  async createPagePublicShare({
    fileUuid,
    expiresAt = null,
  }: ICreatePagePublicShareProps): Promise<ICreatedPagePublicShare> {
    const response = await this.apiClient.post<ICreatedPagePublicShare>(
      pagePublicShareUrl(fileUuid),
      { expiresAt },
    );
    return response.data;
  }

  async revokePagePublicShare(fileUuid: string): Promise<void> {
    await this.apiClient.post(revokePagePublicShareUrl(fileUuid), {});
  }

  // NOTE: Anonymous viewer side. All of these opt out of the client's session
  // auth handling (skipAuthHandling): the viewer has no NSAI session, and a 401
  // means a bad or rotated password, not sign-out.

  async getSharedPageStatus(
    shareToken: string,
  ): Promise<IPagePublicShareStatus> {
    const response = await this.apiClient.get<IPagePublicShareStatus>(
      sharedPageStatusUrl(shareToken),
      { skipAuthHandling: true },
    );
    return response.data;
  }

  async verifySharedPagePassword({
    shareToken,
    password,
  }: IVerifyPagePublicSharePasswordProps): Promise<IPagePublicShareVerifyResponse> {
    const response = await this.apiClient.post<IPagePublicShareVerifyResponse>(
      sharedPageVerifyUrl(shareToken),
      { password },
      { skipAuthHandling: true },
    );
    return response.data;
  }

  // NOTE: Returns the page SOURCE as a JSON string. The caller must render it
  // in the shared sandboxed frame (prepareHtmlPreviewDocument + the preview
  // CSP), never inject it into the app's own document.
  async getSharedPageContent({
    shareToken,
    accessToken,
  }: IGetSharedPageContentProps): Promise<ISharedPageContent> {
    const response = await this.apiClient.get<ISharedPageContent>(
      sharedPageContentUrl(shareToken),
      {
        skipAuthHandling: true,
        headers: { [PAGE_SHARE_ACCESS_TOKEN_HEADER]: accessToken },
      },
    );
    return response.data;
  }
}
